import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FALLBACK_AFTER = 60;
const detail = (e) => e?.response?.data?.detail || e?.message || "Something went wrong.";
const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || "").trim());

function useTick(n, set) {
  useEffect(() => {
    if (n <= 0) return undefined;
    const t = setTimeout(() => set(n - 1), 1000);
    return () => clearTimeout(t);
  }, [n, set]);
}

// `email` enables the "send code by email" fallback; omit it to allow SMS only.
export default function PhoneOtpField({ phone, setPhone, token, setToken, email, testIdPrefix = "otp", className = "" }) {
  const [sent, setSent] = useState(false);
  const [mode, setMode] = useState("sms");
  const [via, setVia] = useState("sms");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [fallbackIn, setFallbackIn] = useState(-1);
  useTick(cooldown, setCooldown);
  useTick(fallbackIn, setFallbackIn);

  const reset = () => { setToken(""); setSent(false); setCode(""); setMsg(""); setErr(""); setMode("sms"); setFallbackIn(-1); setCooldown(0); };
  const onPhone = (v) => { setPhone(v); reset(); };
  const allowFallback = email !== undefined && !token && fallbackIn === 0 && mode === "sms";

  const send = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const { data } = await axios.post(`${API}/auth/otp/send`, { phone });
      setSent(true); setMode("sms"); setCode(""); setCooldown(data.resend_in || 60); setFallbackIn(FALLBACK_AFTER);
      setMsg(`OTP sent to ${data.phone}. Valid for 10 minutes.`);
    } catch (e) {
      setErr(detail(e));
      if (e?.response?.status !== 429 && e?.response?.status !== 400) setFallbackIn(0);
    } finally { setBusy(false); }
  };

  const sendEmail = async () => {
    if (!validEmail(email)) { setErr("Enter your email address above first, then request the email code."); return; }
    setBusy(true); setErr(""); setMsg("");
    try {
      const { data } = await axios.post(`${API}/auth/otp/send-email`, { phone, email: email.trim() });
      setSent(true); setMode("email"); setCode(""); setCooldown(data.resend_in || 60);
      setMsg(`Code sent to ${data.email}. Check your inbox (and spam). Valid for 10 minutes.`);
    } catch (e) { setErr(detail(e)); } finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true); setErr("");
    try {
      const path = mode === "email" ? "verify-email" : "verify";
      const { data } = await axios.post(`${API}/auth/otp/${path}`, mode === "email" ? { phone, email: email.trim(), code } : { phone, code });
      setVia(mode); setToken(data.phone_token); setMsg("");
    } catch (e) { setErr(detail(e)); } finally { setBusy(false); }
  };

  const sendLabel = cooldown > 0 ? `${sent ? "Resend" : "Send OTP"} in ${cooldown}s` : sent ? (mode === "email" ? "Resend email" : "Resend OTP") : "Send OTP";
  return (
    <div className={`otp-field ${className}`} data-testid={`${testIdPrefix}-field`}>
      <label>Mobile number (required)
        <div className="otp-row">
          <input data-testid={`${testIdPrefix}-phone`} value={phone} onChange={(e) => onPhone(e.target.value)}
            placeholder="10-digit mobile" inputMode="tel" required disabled={!!token} />
          {token ? (
            <span className="otp-verified" data-testid={`${testIdPrefix}-verified`}><CheckCircle2 size={15} /> {via === "email" ? "Email verified" : "Verified"}</span>
          ) : (
            <button type="button" className="otp-btn" data-testid={`${testIdPrefix}-send`}
              disabled={busy || cooldown > 0 || phone.replace(/\D/g, "").length < 10} onClick={mode === "email" ? sendEmail : send}>
              {sendLabel}
            </button>
          )}
        </div>
      </label>
      {token && via === "email" && (
        <div className="otp-msg" data-testid={`${testIdPrefix}-email-note`} style={{color:"#B45309"}}>
          Your mobile number will be saved as not verified. You can verify it by SMS later from your workspace.
        </div>
      )}
      {token && <button type="button" className="otp-link" data-testid={`${testIdPrefix}-change`} onClick={() => onPhone(phone)}>Change number</button>}
      {sent && !token && (
        <div className="otp-row">
          <input data-testid={`${testIdPrefix}-code`} value={code} maxLength={6} inputMode="numeric"
            placeholder={mode === "email" ? "Enter 6-digit code from email" : "Enter 6-digit OTP"}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
          <button type="button" className="otp-btn" data-testid={`${testIdPrefix}-verify`} disabled={busy || code.length !== 6} onClick={verify}>Verify</button>
        </div>
      )}
      {msg && <div className="otp-msg" data-testid={`${testIdPrefix}-msg`}>{msg}</div>}
      {err && <div className="auth-error" data-testid={`${testIdPrefix}-error`}>{err}</div>}
      {email !== undefined && !token && sent && mode === "sms" && fallbackIn > 0 && (
        <div className="otp-msg" data-testid={`${testIdPrefix}-fallback-wait`} style={{color:"#6b7280"}}>No SMS? You can get the code by email in {fallbackIn}s.</div>
      )}
      {allowFallback && (
        <button type="button" className="otp-link" data-testid={`${testIdPrefix}-email-fallback`} disabled={busy}
          onClick={sendEmail} style={{display:"inline-flex",alignItems:"center",gap:5}}>
          <Mail size={13}/> Didn't get the SMS? Send the code to {validEmail(email) ? email.trim() : "my email"} instead
        </button>
      )}
      {mode === "email" && !token && (
        <button type="button" className="otp-link" data-testid={`${testIdPrefix}-back-sms`} onClick={() => { setMode("sms"); setSent(false); setCode(""); setMsg(""); setErr(""); setFallbackIn(0); }}>
          Use SMS instead
        </button>
      )}
    </div>
  );
}
